import GetApi from "../api/GetApi";

export default class UserActivityMonitor {
  private isActive: boolean;
  private callApiEverySeconds: number;
  private intervalId: number | null = null;
  private eventListeners: Array<{ event: string; handler: () => void }> = [];

  constructor(callEverySeconds?: number) {
    this.callApiEverySeconds = callEverySeconds ?? 300; // Default 5 minutes for Lenders App
    this.isActive = true;
    this.init();
  }

  private init(): void {
    this.setupActivityListeners();
    this.startApiCallInterval();
  }

  private setupActivityListeners(): void {
    const events = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
    events.forEach(event => {
      const handler = () => this.handleActivity();
      window.addEventListener(event, handler);
      this.eventListeners.push({ event, handler });
    });
  }

  private handleActivity(): void {
    if (document.visibilityState === 'visible') {
      this.isActive = true;
    }
  }

  private startApiCallInterval(): void {
    this.makeApiCall();
    this.intervalId = window.setInterval(() => {
      if (this.isActive) {
        this.makeApiCall();
        this.isActive = false;
      }
    }, this.callApiEverySeconds * 1000);
  }

  public destroy(): void {
    // Clear the interval
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Remove all event listeners
    this.eventListeners.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler);
    });
    this.eventListeners = [];
  }

  private makeApiCall(): void {
    // Skip API calls in dev mode with auth bypass
    if (import.meta.env.VITE_DEV_AUTH_BYPASS === 'true') {
      if (import.meta.env.DEV) console.log('🔧 DEV MODE: Skipping user-is-active API call');
      return;
    }

    // Don't make API calls on login/logout pages
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    if (view === 'login' || view === 'logout') {
      return;
    }

    if (import.meta.env.DEV) console.log('⏱️ UserActivityMonitor: Making user-is-active API call');
    GetApi(`${import.meta.env.VITE_API_URL}/verascore/user-is-active`,
      (data: any) => {
        // Success - check if session is still active
        if (data.active === false) {
          if (import.meta.env.DEV) console.warn('⚠️ UserActivityMonitor: Session expired, redirecting to logout...');
          window.history.replaceState({}, '', '?view=logout&reason=timeout');
          window.dispatchEvent(new PopStateEvent('popstate'));
        } else {
          if (import.meta.env.DEV) console.log('✅ UserActivityMonitor: Session active');
        }
      },
      () => {
        // Fail - might need to redirect to login
        if (import.meta.env.DEV) console.warn('⚠️ UserActivityMonitor: user-is-active API call failed');
      },
      () => {
        // Reject - network or other error
        if (import.meta.env.DEV) console.warn('⚠️ UserActivityMonitor: user-is-active API call rejected');
      });
  }
}

